import { Suspense, useEffect, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import moment from 'moment-timezone';

import './App.scss';

import { setSalesChannelLoading } from './_actions/salesChannel.actions';
import { useDispatch, useSelector } from 'react-redux';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { checkoutUiUrl, getChannelByUrl } from './config';
import { setUserAccountLoading } from './_actions/userAccount.actions';
import { getUserAccount } from './_services/userAccount.service';
import CookieConsentComponent from './_components/CookieConsentComponent';
import { setUpcomingEventsLoading } from './_actions/upcomingEvents.actions';
import { getUpcomingEvents } from './_services/upcomingEvents.service';
import { getSalesChannelByUrl } from './_services/salesChannel.service';
import { useQueryparam } from './_hooks/useQueryparam';
import Loader from './_components/Loader/Loader';
import { isErrorOnFetchSalesChannelSelector, userAccountSelector } from './_selectors';
import Routes from './Routes';
import NoErrorLoader from './_components/NoErrorLoader/NoErrorLoader';
import { useIsTicketmaster } from './_hooks/useIsTicketmaster';
import { useIsOkta } from './_hooks/useIsOkta';
import { useIsTextBuy } from './_hooks/useIsTextBuy';
import { useSalesChannel } from './_hooks/useSalesChannel';
import { useIsLoggedIn } from './_hooks/useIsLoggedIn';
import { useIsSeatGeek } from './_hooks/useIsSeatGeek';
import { useIsOktaSSOforPassSubscription } from './_hooks/useIsOktaSSOforPassSubscription';
import { setOneApiUrl } from './oneApi';
import * as ROUTES from './_constants/routes';

const App: React.FC = () => {
    // NOTE: rockets.pogoseat.com: THIS VALUE IS HARDCODED FOR TESTING PURPOSE in localhost
    // const locationHost = 'giants-test' as any;
    const locationHost = window.location.host;
    const salesChannel = getChannelByUrl(locationHost);

    if (!salesChannel) {
        throw new Error('Could not find sales channel config for ' + window.location.host);
    }

    const { isDemo, fullName, googleAnalyticsId } = salesChannel;
    setOneApiUrl(isDemo ? process.env.REACT_APP_ONE_TEST_URL! : process.env.REACT_APP_ONE_URL!);

    const isEuropeanTimeZone = moment.tz.guess().startsWith('Europe');
    // Inside the embed iframe, cookie consent is gated on the visitor's IP country (handled in
    // OptInEmbedPage), not the browser timezone — so suppress the timezone-driven banner here.
    const isEmbedRoute = window.location.pathname.replace(/\/+$/, '') === ROUTES.EMBED;

    const isUserLoggedIn = useIsLoggedIn();
    const dispatch = useDispatch();
    const query = useQueryparam();
    const [stripePromise] = useState(() =>
        loadStripe((!isDemo ? process.env.REACT_APP_STRIPE_KEY_LIVE : process.env.REACT_APP_STRIPE_KEY_TEST) || ''),
    );

    const isTextBuy = useIsTextBuy();
    const isTicketmaster = useIsTicketmaster();
    const isOkta = useIsOkta();
    const isBallpark = useIsOktaSSOforPassSubscription();
    const isSeatGeek = useIsSeatGeek();

    const isErrorOnFetchSalesChannel = useSelector(isErrorOnFetchSalesChannelSelector);
    const { textBuyUI, salesChannelId, salesChannelConfig } = useSalesChannel();
    const user = useSelector(userAccountSelector).data;
    const isRegisteredUser = user && user.paymentToken && user.completedAt;
    useEffect(() => {
        dispatch(setSalesChannelLoading());
        dispatch(getSalesChannelByUrl({ salesChannelUrl: locationHost }));
        if (window.location.hash && window.location.hash.includes('confirmemail')) {
            window.location.href = window.location.href.replace('register#', '');
        } // redirect to confirm page
        if (window.location.href.includes('pass#confirmemail')) {
            window.location.href = window.location.href.replace('pass#confirmemail', 'pass/confirmemail');
        } // fix wrong confirmemail url in the emails
    }, []);

    useEffect(() => {
        if (salesChannelId) {
            dispatch(setUpcomingEventsLoading());
            dispatch(getUpcomingEvents({ salesChannel }));

            if (!sessionStorage.getItem('channel') && salesChannelConfig?.defaultChannel) {
                sessionStorage.setItem('channel', salesChannelConfig.defaultChannel);
            }
        }
    }, [salesChannelId]);

    useEffect(() => {
        if (isUserLoggedIn && textBuyUI && !user) {
            sessionStorage.setItem('loggedIn', 'true');
            dispatch(setUserAccountLoading());
            dispatch(getUserAccount({ signupToken: sessionStorage.getItem('signupToken') || '' }, salesChannelId));
        }
    }, [isUserLoggedIn, textBuyUI]);

    // cw=1 signals the user arrived via a Chrome Custom Tab — persist so OnboardingPage can close it on Done
    if (query.get('cw') === '1') {
        sessionStorage.setItem('mobileWebView', '1');
    }

    // store queryParams to sessionStorage
    ['referrer', 'source', 'campaign', 'serviceToken', 'channel', 'token', 'tab'].forEach((param) => {
        const queryParamValue = query.get(param);
        if (queryParamValue) {
            sessionStorage.setItem(param, queryParamValue);
        }
        const currentValue = sessionStorage.getItem(param);
        if (!currentValue && !queryParamValue && param === 'source') {
            if ([141, 160].includes(salesChannelId)) {
                sessionStorage.setItem(param, 'studentrushca');
            }
            if ([126, 145, 148, 150].includes(salesChannelId)) {
                sessionStorage.setItem(param, 'lastminute');
            }
            if ([134, 140, 138, 147].includes(salesChannelId)) {
                sessionStorage.setItem(param, 'studentrush');
            }
        }

        if (!currentValue && !queryParamValue && param === 'campaign') {
            if ([138, 147, 149, 150].includes(salesChannelId)) {
                sessionStorage.setItem(param, 'season24-25');
            }
            if ([134, 145, 148, 160].includes(salesChannelId)) {
                sessionStorage.setItem(param, 'web');
            }
        }
    });

    // ?tab= is captured above and consumed from sessionStorage (OptInLoginPage), so drop it
    // from the URL — deep links land on a clean /pass/login like the redirect-stripped routes do.
    if (query.get('tab')) {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('tab');
        window.history.replaceState(null, '', cleanUrl.toString());
    }

    if (query.get('signupToken')) {
        let token = query.get('signupToken') || '';
        // decode and parse sign up token to get phone and email
        token = atob(token);
        const parsedToken = JSON.parse(token);

        sessionStorage.setItem('source', 'attentive');
        if (parsedToken && parsedToken.channel) {
            sessionStorage.setItem('channel', parsedToken.channel);
        }
        if (parsedToken && parsedToken.email) {
            sessionStorage.setItem('email', parsedToken.email);
        }
        if (parsedToken && parsedToken.phone) {
            sessionStorage.setItem('serviceToken', parsedToken.phone.replace(/[ ()-+]/gm, ''));
        }
    }

    const referrer = sessionStorage.getItem('referrer');
    const oneSessionId = sessionStorage.getItem('oneSessionId');
    const signupToken = sessionStorage.getItem('signupToken');
    const loggedIn = sessionStorage.getItem('loggedIn');
    // wait for user to login and redirect to checkout-ui
    if (referrer && oneSessionId && salesChannelId && signupToken && loggedIn) {
        sessionStorage.removeItem('referrer');
        sessionStorage.removeItem('oneSessionId');
        switch (referrer) {
            case 'checkout-ui':
                window.location.href = `${checkoutUiUrl}/bid/${salesChannelId}/landing?oneSessionId=${oneSessionId}`;
                break;
            case 'checkout-ui-seatmap':
                if (salesChannelConfig?.seatmapUrl) {
                    if (process.env.NODE_ENV === 'production') {
                        window.location.href = `${salesChannelConfig.seatmapUrl}?oneSessionId=${oneSessionId}`;
                    } else {
                        window.location.href = `${salesChannelConfig.seatmapUrlQA}?oneSessionId=${oneSessionId}`;
                    }
                }
                break;
            case 'checkout-ui-bidding':
                if (salesChannelConfig?.biddingUrl) {
                    if (process.env.NODE_ENV === 'production') {
                        window.location.href = `${salesChannelConfig.biddingUrl}?oneSessionId=${oneSessionId}`;
                    } else {
                        window.location.href = `${salesChannelConfig.biddingUrlQA}?oneSessionId=${oneSessionId}`;
                    }
                }
                break;
            case 'checkout-ui-drops':
                if (salesChannelConfig?.dropsUrl) {
                    if (process.env.NODE_ENV === 'production') {
                        window.location.href = `${salesChannelConfig.dropsUrl}?oneSessionId=${oneSessionId}`;
                    } else {
                        window.location.href = `${salesChannelConfig.dropsUrlQA}?oneSessionId=${oneSessionId}`;
                    }
                }
                break;
            case 'checkout-ui-marketplace':
                if (salesChannelConfig?.marketplaceUrl) {
                    if (process.env.NODE_ENV === 'production') {
                        window.location.href = `${salesChannelConfig.marketplaceUrl}?oneSessionId=${oneSessionId}`;
                    } else {
                        window.location.href = `${salesChannelConfig.marketplaceUrlQA}?oneSessionId=${oneSessionId}`;
                    }
                }

                break;
            default:
                // See if this is a valid URL
                try {
                    const url = new URL(referrer);

                    if (url.hostname.endsWith('pogoseat.com') || url.hostname.endsWith('pogoseat.localhost')) {
                        window.location.href = `${referrer}?oneSessionId=${oneSessionId}`;
                    } else {
                        console.error(
                            'Referrer is a valid URL but for security reasons we are not redirecting to it.',
                            referrer,
                        );
                    }
                } catch (e) {
                    console.error('Referrer is an invalid handle and/or URL.', referrer);
                }

                break;
        }
    }

    if (isErrorOnFetchSalesChannel) {
        return <Loader message="Failed to fetch details for salesChannelId!" />;
    }

    if (isTextBuy === null || isTicketmaster === null || isOkta === null || isSeatGeek === null) {
        return (
            <NoErrorLoader
                loading={isTextBuy === null || isTicketmaster === null || isOkta === null || isSeatGeek === null}
            />
        );
    }

    return (
        <div className="App">
            {isTextBuy && (
                <HelmetProvider>
                    <Helmet>
                        <title>{`Sign up with your mobile phone number and we'll text you when tickets are available.`}</title>
                        <meta
                            name="description"
                            content={`The ${fullName} have teamed up with Pogoseat to give fans exclusive access to buying tickets. All it takes is a simple text message to secure your seats!`}
                        />
                        {/* Global site tag (gtag.js) - Google Analytics  */}
                        {googleAnalyticsId && (
                            <script
                                async
                                type={isEuropeanTimeZone ? 'text/plain' : 'text/javascript'}
                                data-cookiecategory="analytics"
                                src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`}
                            ></script>
                        )}
                        <script
                            type={isEuropeanTimeZone ? 'text/plain' : 'text/javascript'}
                            data-cookiecategory="analytics"
                        >
                            {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());
          gtag('config', "${googleAnalyticsId}");
        `}
                        </script>
                    </Helmet>
                </HelmetProvider>
            )}

            <Suspense fallback={<Loader />}>
                <Elements stripe={stripePromise}>
                    {salesChannelId ? (
                        <Routes
                            loggedIn={isUserLoggedIn}
                            isTextBuy={!!isTextBuy}
                            isTicketmaster={!!isTicketmaster}
                            isOkta={!!isOkta}
                            isBallpark={!!isBallpark}
                            isSeatGeek={!!isSeatGeek}
                            isRegisteredUser={!!isRegisteredUser}
                            useOptInLoginPage={!!salesChannelConfig?.useOptInLoginPage}
                            embedEnabled={!!salesChannelConfig?.embed?.enabled}
                        />
                    ) : (
                        <Loader />
                    )}
                </Elements>

                {isEuropeanTimeZone && !isBallpark && !isEmbedRoute && <CookieConsentComponent />}
            </Suspense>
        </div>
    );
};

export default App;
